import React, { useState } from "react";
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, alpha, IconButton, useMediaQuery, useTheme, Avatar,
} from "@mui/material";

import { Link, useNavigate, useLocation } from "react-router-dom";

import DashboardIcon from "@mui/icons-material/Dashboard";
import FlagIcon from "@mui/icons-material/Flag";
import ChatIcon from "@mui/icons-material/Chat";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MenuIcon from "@mui/icons-material/Menu";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import {
  PeopleAlt,
  AssignmentTurnedIn,
  Chat,
  Notifications,
  Settings,
} from "@mui/icons-material";

// IMPORT YOUR LOGO — still used in the mobile top bar
import AskGiiaLogo from "../Assets/home4.png";

const API = "https://backend-global-1vq9.onrender.com";

// ── Helper: build a full URL from a server-stored image path ─────────────────
// Same pattern as AccountSettings — handles relative paths from old uploads
// and full Cloudinary URLs from new ones without any extra logic.
const resolveImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("data:") || path.startsWith("blob:")) return path;
  return `${API}/${String(path).replace(/\\/g, "/").replace(/^\//, "")}`;
};

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  border: "#E5E9F0",
};

const drawerWidth = 260;

const learnerMenuItems = [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/learner-dash" },
  { text: "Mentors", icon: <PeopleIcon />, path: "/mentor" },
  { text: "My Goals", icon: <FlagIcon />, path: "/mygoal" },
  { text: "Chats", icon: <ChatIcon />, path: "/chat" },
  { text: "Requests", icon: <AssignmentTurnedInIcon />, path: "/learner-requests" },
  { text: "Lessons", icon: <SchoolIcon />, path: "/curriculum" },
  { text: "Sessions", icon: <TrendingUpIcon />, path: "/sessions" },
  { text: "Events",    icon: <EventNoteIcon />,    path: "/events" },
  { text: "My Events", icon: <CalendarMonthIcon />, path: "/my-events" },
  // { text: "Project", icon: <AccountTreeIcon />, path: "/currentProjects" },
  { text: "Community", icon: <PeopleIcon />, path: "/discussionForum" },
  { text: "Notifications", icon: <NotificationsIcon />, path: "/notification" },
  { text: "Settings", icon: <SettingsIcon />, path: "/accountSettings" },
];

const mentorMenuItems = [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/mentor-dash" },
  { text: "Mentees", icon: <PeopleAlt />, path: "/learner-list" },
  { text: "Requests", icon: <AssignmentTurnedIn />, path: "/mentorsiderequest" },
  { text: "Chats", icon: <Chat />, path: "/chat" },
  { text: "Lessons", icon: <SchoolIcon />, path: "/createLessons" },
  { text: "Sessions", icon: <TrendingUpIcon />, path: "/sessions" },
  { text: "Community", icon: <PeopleAlt />, path: "/discussionForum" },
  { text: "Events",    icon: <EventNoteIcon />,    path: "/events" },
  { text: "My Events", icon: <CalendarMonthIcon />, path: "/my-events" },
  { text: "Notifications", icon: <Notifications />, path: "/notification" },
  { text: "Settings", icon: <Settings />, path: "/accountSettings" },
];

export default function SideNavbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  const menuItems =
    user?.role?.toLowerCase() === "mentor" ? mentorMenuItems : learnerMenuItems;

  // Initials fallback for when the user hasn't uploaded a profile picture
  const initials = (user?.fullName || user?.email || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const avatarUrl = resolveImageUrl(user?.profileImage);

  // LOGOUT
  const handleLogout = async () => {
    try {
      await fetch("https://backend-global-1vq9.onrender.com/api/auth/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      localStorage.removeItem("token");
      localStorage.removeItem("user");

      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const drawerContent = (
    <>
      {/* TOP SECTION */}
      <Box>
        {/* BRAND HEADER */}
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            position: "relative",
            overflow: "hidden",

            "&::before": {
              content: '""',
              position: "absolute",
              top: -60,
              right: -40,
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
              pointerEvents: "none",
            },
          }}
        >
          {/* Profile picture + role label, side by side */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1,
            }}
          >
            <Avatar
              src={avatarUrl}
              alt={user?.fullName || user?.email || "User"}
              sx={{
                width: 44,
                height: 44,
                bgcolor: "#fff",
                color: C.primary,
                fontWeight: 800,
                fontSize: 16,
                boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
                flexShrink: 0,
                border: "2px solid rgba(255,255,255,0.35)",
              }}
            >
              {!avatarUrl && initials}
            </Avatar>

            <Typography
              sx={{
                fontWeight: 800,
                color: "#fff",
                fontSize: "1.1rem",
                letterSpacing: -0.2,
                textTransform: "capitalize",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.role || "Guest User"}
            </Typography>
          </Box>

          {/* <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              color: "rgba(255,255,255,0.85)",
              display: "block",
              fontSize: "0.75rem",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user?.email || "No email"}
          </Typography> */}

          {/* <Chip
            label="Active"
            size="small"
            sx={{
              position: "relative",
              zIndex: 1,
              mt: 1.25,
              bgcolor: alpha(C.secondary, 0.85),
              color: "#fff",
              fontWeight: 700,
              height: 22,
              fontSize: "0.7rem",
            }}
          /> */}
        </Box>

        <Divider sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />

        {/* MENU ITEMS */}
        <List sx={{ px: 2, py: 2 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;

            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={() => isMobile && setMobileOpen(false)}
                  sx={{
                    borderRadius: 2,
                    py: 1.2,
                    px: 2,
                    color: isActive ? "#fff" : "rgba(255,255,255,0.7)",
                    bgcolor: isActive ? alpha(C.primaryLight, 0.25) : "transparent",
                    position: "relative",
                    transition: "all 0.2s ease",

                    "&:hover": {
                      bgcolor: alpha(C.primaryLight, 0.18),
                      color: "#fff",
                      transform: "translateX(4px)",
                    },

                    "&::before": isActive
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 4,
                          height: "70%",
                          borderRadius: "0 4px 4px 0",
                          background: `linear-gradient(180deg, ${C.secondary}, ${C.secondaryLight})`,
                        }
                      : {},
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? "#fff" : "rgba(255,255,255,0.7)",
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: isActive ? 700 : 500,
                    }}
                  />

                  {isActive && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: C.secondaryLight,
                        boxShadow: `0 0 8px ${C.secondaryLight}aa`,
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* BOTTOM */}
      <Box>
        <Divider sx={{ bgcolor: "rgba(255,255,255,0.08)", mb: 2 }} />

        <Box sx={{ px: 2, pb: 3 }}>
          <Button
            variant="contained"
            startIcon={<LogoutIcon />}
            fullWidth
            onClick={() => setOpenDialog(true)}
            sx={{
              textTransform: "none",
              py: 1.2,
              borderRadius: 2.5,
              fontWeight: 700,
              fontSize: "0.9rem",
              background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryDark} 100%)`,
              boxShadow: "none",
              "&:hover": {
                background: `linear-gradient(135deg, ${C.secondaryDark} 0%, ${C.secondary} 100%)`,
                transform: "translateY(-1px)",
                boxShadow: `0 4px 14px ${C.secondary}50`,
              },
              transition: "all 0.2s ease",
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      {/* MOBILE TOP BAR */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            height: 64,
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            paddingTop: 5,
            paddingBottom: 5,
            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="img"
              src={AskGiiaLogo}
              alt="Giia Logo"
              sx={{
                width: 40,
                height: 40,
                objectFit: "contain",
                borderRadius: "10px",
                bgcolor: "#fff",
                p: 0.5,
              }}
            />

            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: "1rem",
                letterSpacing: -0.2,
              }}
            >
              giiconnect
            </Typography>
          </Box>

          <IconButton onClick={handleDrawerToggle}>
            <MenuIcon sx={{ color: "#fff" }} />
          </IconButton>
        </Box>
      )}

      {/* DRAWER */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,

          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: `linear-gradient(180deg, ${C.primaryDark} 0%, #0A1A4D 100%)`,
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            borderRight: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* LOGOUT DIALOG */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1, minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: C.textPrimary }}>
          Confirm Logout
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: C.textSecondary, lineHeight: 1.7 }}
          >
            Are you sure you want to logout? You will need to log in again to
            access your account. Any unsaved changes may be lost.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
            p: 3,
            pt: 2,
          }}
        >
          <Button
            onClick={handleLogout}
            variant="contained"
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 700,
              py: 1.2,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryDark} 100%)`,
              boxShadow: "none",
              "&:hover": {
                background: `linear-gradient(135deg, ${C.secondaryDark} 0%, ${C.secondary} 100%)`,
                boxShadow: `0 4px 14px ${C.secondary}40`,
              },
            }}
          >
            Yes, Logout
          </Button>

          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 700,
              py: 1.2,
              borderRadius: 2.5,
              borderWidth: 1.5,
              borderColor: C.border,
              color: C.textSecondary,

              "&:hover": {
                borderWidth: 1.5,
                borderColor: C.textSecondary,
                bgcolor: alpha(C.textSecondary, 0.05),
              },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}